import React from "react";
import { FormControl, FormLabel } from "@mui/material";
import styles from "./AddressPopup.module.css";
import BillingField from "./BillingField";
import { hasFieldValue } from "./utils/billingValidation";

/**
 * BillingForm
 *
 * Presentational billing-address layout for AddressPopup. Renders the standard
 * fields plus custom fields (first one beside VAT, the rest two per row).
 *
 * Props:
 *   billingAddress         – current form values
 *   updateBillingField     – (key, value) updater for a named field
 *   updateCustomFieldValue – (index, value) updater for a custom field
 *   isClicked              – show required-field errors after a submit attempt
 *   vatRequired            – whether VAT ID is required
 */
function BillingForm({
  billingAddress,
  updateBillingField,
  updateCustomFieldValue,
  isClicked,
  vatRequired,
}) {
  const change = (key) => (e) => updateBillingField(key, e.target.value);
  const requiredError = (key) =>
    isClicked && !hasFieldValue(billingAddress[key]);
  const customFields = billingAddress.custom_fields || [];

  const renderCustomField = (field, index) => {
    if (!field) {
      return null;
    }

    return (
      <BillingField
        key={`custom-field-${index}`}
        label={`${field.label || "Custom Field"}${field.required ? "*" : ""}`}
        value={field.value || ""}
        onChange={(e) => updateCustomFieldValue(index, e.target.value)}
        error={isClicked && field.required && !hasFieldValue(field.value)}
        data-testid={`address-input-custom-field-${index}`}
        labelClassName={styles.text_label}
        formControlClassName={styles.half_form}
      />
    );
  };

  const extraCustomFieldRows = [];
  for (let index = 1; index < customFields.length; index += 2) {
    extraCustomFieldRows.push(
      <div className={styles.address_row} key={`custom-fields-row-${index}`}>
        {renderCustomField(customFields[index], index)}
        {renderCustomField(customFields[index + 1], index + 1)}
      </div>,
    );
  }

  return (
    <>
      <div className={styles.address_row}>
        <BillingField
          label="Name*"
          required
          value={billingAddress.name}
          onChange={change("name")}
          error={requiredError("name")}
          data-testid="address-input-name"
          labelClassName={styles.text_label}
          formControlClassName={styles.half_form}
        />
        <BillingField
          label="Email*"
          type="email"
          required
          value={billingAddress.email}
          onChange={change("email")}
          error={requiredError("email")}
          data-testid="address-input-email"
          labelClassName={styles.text_label}
          formControlClassName={styles.half_form}
        />
      </div>
      <div className={styles.address_row}>
        <BillingField
          label="Address Line 1 *"
          value={billingAddress.address_line_1}
          onChange={change("address_line_1")}
          error={requiredError("address_line_1")}
          data-testid="address-input-address-line-1"
          labelClassName={styles.text_label}
          fullWidth
          inputFullWidth
        />
      </div>
      <div className={styles.address_row}>
        <BillingField
          label="Address Line 2"
          value={billingAddress.address_line_2}
          onChange={change("address_line_2")}
          data-testid="address-input-address-line-2"
          labelClassName={styles.text_label}
          fullWidth
          inputFullWidth
        />
      </div>
      <div className={styles.address_row}>
        <div className={styles.half_form}>
          <BillingField
            label="City*"
            required
            value={billingAddress.city}
            onChange={change("city")}
            error={requiredError("city")}
            data-testid="address-input-city"
            labelClassName={styles.text_label}
            fullWidth
          />
        </div>
        <div className={`${styles.half_form} ${styles.address_container} `}>
          <BillingField
            label="Postal Code*"
            required
            value={billingAddress.postal_code}
            onChange={change("postal_code")}
            error={requiredError("postal_code")}
            data-testid="address-input-postal-code"
            labelClassName={styles.text_label}
            formControlStyle={{ width: "45%" }}
          />
          <FormControl style={{ width: "25%" }}>
            <FormLabel>Country*</FormLabel>
            <p
              className={styles.country_text}
              data-testid="address-country-display"
            >
              {" "}
              {billingAddress.country}
            </p>
          </FormControl>
        </div>
      </div>
      <div className={styles.address_row}>
        <BillingField
          label={`VAT ID ${vatRequired ? " *" : ""}`}
          value={billingAddress.vat_id}
          onChange={change("vat_id")}
          error={
            isClicked && vatRequired && !hasFieldValue(billingAddress.vat_id)
          }
          data-testid="address-input-vat-id"
          labelClassName={styles.text_label}
          formControlClassName={styles.half_form}
        />
        {renderCustomField(customFields[0], 0)}
      </div>
      {extraCustomFieldRows}
    </>
  );
}

export default BillingForm;
